import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../../../../api";
import { useAtom, useAtomValue } from "jotai";
import { AccountAtom, PricingQueueAtom } from "../../../../store";
import { useTransactions } from "../../../../hooks/useTransactions";
import { usePositions } from "../../../../hooks";
import mixpanel from "mixpanel-browser";

const IMPORT_SUCCESS_CLOSE_MS = 1500;

export function useAIUploadController(props: {
  closeModal: () => void;
  selectedBook: string;
  calculateStrategy: { value: string; label: string };
  selectedAccountForUpload: any;
}) {
  const [, setPricingQueueState] = useAtom(PricingQueueAtom);
  const accountState = useAtomValue(AccountAtom);
  const transactions = useTransactions();
  const positions = usePositions();

  const [previewTrades, setPreviewTrades] = useState<any[]>([]);
  const [previewCashflows, setPreviewCashflows] = useState<any[]>([]);
  const [brokerDetected, setBrokerDetected] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [importSuccess, setImportSuccess] = useState(false);
  const [successTradesCount, setSuccessTradesCount] = useState(0);
  const [successCashflowsCount, setSuccessCashflowsCount] = useState(0);

  const mountedRef = useRef(true);
  const successCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (successCloseTimeoutRef.current != null) {
        clearTimeout(successCloseTimeoutRef.current);
        successCloseTimeoutRef.current = null;
      }
    };
  }, []);

  const accountId =
    props.selectedAccountForUpload?.id || accountState?.selectedAccount?.id;

  const resetPreviewState = useCallback(() => {
    setPreviewTrades([]);
    setPreviewCashflows([]);
    setBrokerDetected("");
    setErrorMessage("");
    setImportSuccess(false);
    setSuccessTradesCount(0);
    setSuccessCashflowsCount(0);
  }, []);

  const previewFile = async (file: File) => {
    if (!accountId) {
      setErrorMessage("Select a portfolio first.");
      return;
    }
    mixpanel.track("AI Upload Preview Attempt", {});
    setErrorMessage("");
    setImportSuccess(false);
    setLoading(true);
    setPreviewTrades([]);
    setPreviewCashflows([]);
    setBrokerDetected("");

    const fd = new FormData();
    fd.append("file", file);

    try {
      const res: any = await api.aiUploadPreview(fd, accountId);
      if (res?.success === true) {
        mixpanel.track("AI Upload Preview Success", {
          brokerDetected: res.brokerDetected,
        });
        setPreviewTrades(res.trades || []);
        setPreviewCashflows(res.cashflows || []);
        setBrokerDetected(res.brokerDetected || "");
      } else {
        mixpanel.track("AI Upload Preview Failure", { message: res?.message });
        setErrorMessage(res?.message || "Could not map file");
      }
    } catch (e: any) {
      setErrorMessage(e?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const confirmUpload = async () => {
    if (
      !accountId ||
      (previewTrades.length === 0 && previewCashflows.length === 0)
    )
      return;

    mixpanel.track("AI Upload Confirm Attempt", {
      rows: previewTrades.length,
      cashflows: previewCashflows.length,
    });
    setUploading(true);
    setErrorMessage("");

    try {
      const res: any = await api.aiUploadConfirm(
        previewTrades,
        previewCashflows,
        accountId,
        props.selectedBook,
        props.calculateStrategy?.value || "NONE"
      );

      if (res?.success === true) {
        mixpanel.track("AI Upload Confirm Success", {});
        const tCount = (res.trades || []).length;
        const cfCount = (res.cashflows || []).length;
        transactions.setTrades(res.trades || []);
        transactions.setCashflows(res.cashflows || []);
        positions.fetchPositions(accountId);
        transactions.getBooks(accountId);
        setPricingQueueState({ active: true });
        setSuccessTradesCount(tCount);
        setSuccessCashflowsCount(cfCount);
        setImportSuccess(true);
        if (successCloseTimeoutRef.current != null) {
          clearTimeout(successCloseTimeoutRef.current);
        }
        successCloseTimeoutRef.current = window.setTimeout(() => {
          successCloseTimeoutRef.current = null;
          if (!mountedRef.current) return;
          props.closeModal();
        }, IMPORT_SUCCESS_CLOSE_MS);
      } else {
        setErrorMessage(res?.message || "Import failed");
      }
    } catch (e: any) {
      setErrorMessage(e?.message || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const removeTradeRow = (index: number) => {
    setPreviewTrades((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTradeRow = (index: number, field: string, value: unknown) => {
    setPreviewTrades((prev) => {
      const next = [...prev];
      const row = { ...next[index], [field]: value };
      if (field === "tradeDate" && typeof value === "string") {
        row.tradeDate = new Date(value);
      }
      next[index] = row;
      return next;
    });
  };

  return {
    previewTrades,
    setPreviewTrades,
    previewCashflows,
    setPreviewCashflows,
    brokerDetected,
    loading,
    uploading,
    errorMessage,
    setErrorMessage,
    previewFile,
    confirmUpload,
    removeTradeRow,
    updateTradeRow,
    accountId,
    resetPreviewState,
    importSuccess,
    successTradesCount,
    successCashflowsCount,
  };
}
